import React from "react";
import styled from "styled-components";

const SkeletonWrapper = styled.div`
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }

  .skeleton-base {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 1000px 100%;
    animation: shimmer 2s infinite;
    border-radius: 8px;
  }
`;

const ShopContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;

  @media (max-width: 768px) {
    padding: 20px 15px;
  }
`;

const ShopHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;
`;

const HeaderSkeleton = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  flex: 1;

  .count {
    height: 20px;
    width: 150px;
  }

  .toggle {
    display: flex;
    gap: 10px;

    .btn {
      width: 40px;
      height: 40px;
    }
  }

  .sort {
    display: flex;
    gap: 10px;
    align-items: center;

    .label {
      height: 20px;
      width: 60px;
    }

    .select {
      height: 40px;
      width: 150px;
    }
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 30px;

  @media (max-width: 1024px) {
    grid-template-columns: 200px 1fr;
    gap: 20px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const FilterSkeleton = styled.div`
  .title {
    height: 20px;
    width: 100px;
    margin-bottom: 15px;
  }

  .item {
    height: 18px;
    width: 80%;
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const ProductGridSkeleton = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 25px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 20px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const ProductCardSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  .image {
    width: 100%;
    height: 250px;
    border-radius: 8px;
  }

  .category {
    height: 12px;
    width: 60px;
    margin-top: 8px;
  }

  .title {
    height: 18px;
    width: 100%;
  }

  .rating {
    height: 14px;
    width: 80px;
  }

  .price {
    height: 20px;
    width: 70px;
    margin-top: 10px;
  }

  .button {
    height: 40px;
    width: 100%;
    margin-top: 10px;
  }
`;

const ProductListSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ListItemSkeleton = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr auto;
  gap: 20px;
  padding: 20px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;

  @media (max-width: 768px) {
    grid-template-columns: 150px 1fr;
    gap: 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }

  .image {
    width: 200px;
    height: 200px;
    border-radius: 4px;

    @media (max-width: 768px) {
      width: 150px;
      height: 150px;
    }

    @media (max-width: 480px) {
      width: 100%;
      height: 200px;
    }
  }

  .details {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .category {
      height: 12px;
      width: 60px;
    }

    .title {
      height: 18px;
      width: 100%;
    }

    .text {
      height: 14px;
      width: 100%;

      &:last-child {
        width: 80%;
      }
    }
  }

  .price {
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: flex-end;

    .amount {
      height: 24px;
      width: 80px;
    }

    .original {
      height: 14px;
      width: 70px;
    }

    .button {
      height: 40px;
      width: 120px;
      margin-top: 10px;
    }

    @media (max-width: 768px) {
      align-items: flex-start;
    }
  }
`;

const PaginationSkeleton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 40px;

  .info {
    height: 18px;
    width: 200px;
    margin-right: 20px;
  }

  .btn {
    width: 40px;
    height: 40px;
  }
`;

const ShopSkeleton = ({ viewMode = "grid" }) => {
  return (
    <SkeletonWrapper>
      <ShopContainer>
        <ShopHeader>
          <HeaderSkeleton>
            <div className="skeleton-base count" />
            <div className="skeleton-base toggle">
              <div className="skeleton-base btn" />
              <div className="skeleton-base btn" />
            </div>
            <div className="skeleton-base sort">
              <div className="skeleton-base label" />
              <div className="skeleton-base select" />
            </div>
          </HeaderSkeleton>
        </ShopHeader>

        <ContentWrapper>
          <Sidebar>
            <FilterSkeleton>
              <div className="skeleton-base title" />
              <div className="skeleton-base item" />
              <div className="skeleton-base item" />
              <div className="skeleton-base item" />
            </FilterSkeleton>

            <FilterSkeleton>
              <div className="skeleton-base title" />
              <div className="skeleton-base item" style={{ width: "100%" }} />
            </FilterSkeleton>

            <FilterSkeleton>
              <div className="skeleton-base title" />
              <div className="skeleton-base item" />
              <div className="skeleton-base item" />
              <div className="skeleton-base item" />
            </FilterSkeleton>
          </Sidebar>

          {viewMode === "grid" ? (
            <ProductGridSkeleton>
              {Array.from({ length: 12 }).map((_, i) => (
                <ProductCardSkeleton key={i}>
                  <div className="skeleton-base image" />
                  <div className="skeleton-base category" />
                  <div className="skeleton-base title" />
                  <div className="skeleton-base rating" />
                  <div className="skeleton-base price" />
                  <div className="skeleton-base button" />
                </ProductCardSkeleton>
              ))}
            </ProductGridSkeleton>
          ) : (
            <ProductListSkeleton>
              {Array.from({ length: 3 }).map((_, i) => (
                <ListItemSkeleton key={i}>
                  <div className="skeleton-base image" />
                  <div className="details">
                    <div className="skeleton-base category" />
                    <div className="skeleton-base title" />
                    <div className="skeleton-base text" />
                    <div className="skeleton-base text" />
                  </div>
                  <div className="price">
                    <div className="skeleton-base amount" />
                    <div className="skeleton-base original" />
                    <div className="skeleton-base button" />
                  </div>
                </ListItemSkeleton>
              ))}
            </ProductListSkeleton>
          )}

          <PaginationSkeleton>
            <div className="skeleton-base info" />
            <div className="skeleton-base btn" />
            <div className="skeleton-base btn" />
            <div className="skeleton-base btn" />
          </PaginationSkeleton>
        </ContentWrapper>
      </ShopContainer>
    </SkeletonWrapper>
  );
};

export default ShopSkeleton;
